<script setup lang="ts">
/**
 * Die Suche auf Cmd+K.
 *
 * EIN FELD FÜR ALLES, was man gerade tun oder finden kann: Seiten, Felder,
 * Projekte, Handgriffe. Sanity legt seine globale Suche auf dasselbe Kürzel,
 * Contentful hat sie oben in der Leiste, Linear und Notion haben das Muster
 * groß gemacht. Der Grund ist immer derselbe: Eine Oberfläche wächst, eine
 * Leiste nicht.
 *
 * SIE HÄNGT IN App.vue, also über allem — auch über dem Editor, der den ganzen
 * Bildschirm füllt. Was sie anbietet, meldet jede Ansicht selbst an
 * (`useBefehle`).
 *
 * Kein Fokus-Käfig aus Bibliotheken: Das Feld bekommt den Fokus, Escape und ein
 * Klick daneben schließen, die Pfeiltasten laufen durch die Liste. Mehr braucht
 * ein Dialog aus einem Eingabefeld und einer Liste nicht.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBefehle, type Befehl } from '@/composables/useBefehle'
import { suche } from '@/lib/befehlssuche'

const { t } = useI18n()
const befehle = useBefehle()
const frage = ref('')
const zeiger = ref(0)
const feld = ref<HTMLInputElement | null>(null)
const liste = ref<HTMLElement | null>(null)

const treffer = computed(() => suche(befehle.alle.value, frage.value))

/** Nach Gruppen sortiert, aber in der Reihenfolge der Treffer. */
const gruppiert = computed(() => {
  const aus: { name: string; eintraege: Befehl[] }[] = []
  for (const b of treffer.value) {
    const da = aus.find((g) => g.name === b.gruppe)
    if (da) da.eintraege.push(b)
    else aus.push({ name: b.gruppe, eintraege: [b] })
  }
  return aus
})

/** Dieselbe Reihenfolge wie gezeichnet — daran hängen die Pfeiltasten. */
const flach = computed(() => gruppiert.value.flatMap((g) => g.eintraege))

watch(frage, () => { zeiger.value = 0 })
watch(() => befehle.offen.value, async (auf) => {
  if (!auf) return
  frage.value = ''
  zeiger.value = 0
  await nextTick()
  feld.value?.focus()
})

function waehle(b: Befehl | undefined): void {
  if (b === undefined) return
  befehle.schliessen()
  b.tun()
}

function beiTaste(ev: KeyboardEvent): void {
  // Öffnen darf man von überall, auch aus einem Textfeld heraus.
  if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k') {
    ev.preventDefault()
    if (befehle.offen.value) befehle.schliessen()
    else befehle.oeffnen()
    return
  }
  if (!befehle.offen.value) return
  if (ev.key === 'Escape') {
    ev.preventDefault()
    befehle.schliessen()
    return
  }
  if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
    ev.preventDefault()
    const anzahl = flach.value.length
    if (anzahl === 0) return
    const schritt = ev.key === 'ArrowDown' ? 1 : -1
    zeiger.value = (zeiger.value + schritt + anzahl) % anzahl
    void nextTick(() => {
      // jsdom kennt scrollIntoView nicht, und ein Fehler hier würde die
      // Tastenbedienung mitreißen.
      const zeile = liste.value?.querySelector('[data-am-zug="ja"]')
      if (zeile instanceof HTMLElement && typeof zeile.scrollIntoView === 'function') {
        zeile.scrollIntoView({ block: 'nearest' })
      }
    })
    return
  }
  if (ev.key === 'Enter') {
    ev.preventDefault()
    waehle(flach.value[zeiger.value])
  }
}

onMounted(() => document.addEventListener('keydown', beiTaste))
onBeforeUnmount(() => document.removeEventListener('keydown', beiTaste))

function nummer(gruppe: number, i: number): number {
  let vor = 0
  for (let g = 0; g < gruppe; g++) vor += gruppiert.value[g]?.eintraege.length ?? 0
  return vor + i
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="befehle.offen.value"
      class="fixed inset-0 z-[100] bg-black/40 backdrop-blur-[2px] flex items-start justify-center px-4 pt-[12vh]"
      @click.self="befehle.schliessen()"
    >
      <div
        class="w-full max-w-xl rounded-xl border border-line bg-surface shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        :aria-label="t('suche.titel')"
      >
        <div class="flex items-center gap-2 px-3 border-b border-line">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               class="text-fg-muted shrink-0" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="feld"
            v-model="frage"
            type="text"
            class="flex-1 bg-transparent py-3 text-sm text-fg placeholder:text-fg-muted
                   focus:outline-none"
            :placeholder="t('suche.platzhalter')"
            :aria-label="t('kopf.suchen')"
          >
          <kbd class="hidden sm:inline text-[10px] font-mono text-fg-muted
                      border border-line rounded px-1.5 py-0.5">esc</kbd>
        </div>

        <div ref="liste" class="max-h-[50vh] overflow-y-auto py-1">
          <p
            v-if="flach.length === 0"
            class="px-4 py-6 text-center text-sm text-fg-muted"
          >{{ t('suche.nichts') }}</p>

          <template v-for="(g, gi) in gruppiert" :key="g.name">
            <p class="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase
                      tracking-widest text-fg-muted">{{ g.name }}</p>
            <button
              v-for="(b, i) in g.eintraege"
              :key="b.id"
              type="button"
              :data-am-zug="nummer(gi, i) === zeiger ? 'ja' : 'nein'"
              :class="[
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                nummer(gi, i) === zeiger ? 'bg-primary/10 text-fg' : 'text-fg-mid hover:bg-hover',
              ]"
              role="option"
              :aria-selected="nummer(gi, i) === zeiger"
              @mousemove="zeiger = nummer(gi, i)"
              @click="waehle(b)"
            >
              <span class="truncate">{{ b.label }}</span>
              <span
                v-if="b.hinweis"
                class="ml-auto shrink-0 text-[11px] text-fg-muted truncate max-w-[45%]"
              >{{ b.hinweis }}</span>
            </button>
          </template>
        </div>

        <div class="flex items-center gap-3 border-t border-line px-3 py-1.5
                    text-[10px] text-fg-muted">
          <span>↑ ↓ {{ t('suche.auswaehlen') }}</span>
          <span>↵ {{ t('suche.ausfuehren') }}</span>
          <span class="ml-auto font-mono">⌘K</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>
